import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Eye, Power, Trash2, LogOut, Users, TrendingUp, UserCheck, Clock, FileClock } from "lucide-react";
import { toast } from "sonner";

export default function Platform() {
  const { logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [totals, setTotals] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", admin_email: "", admin_password: "", admin_name: "HR Administrator" });

  const load = () =>
    api
      .get("/tenants/analytics")
      .then((r) => { setTenants(r.data.tenants); setTotals(r.data.totals); })
      .catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/tenants", form); toast.success("Tenant created"); setOpen(false); setForm({ company_name: "", admin_email: "", admin_password: "", admin_name: "HR Administrator" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (t) => { await api.put(`/tenants/${t.id}/status`, { status: t.status === "active" ? "suspended" : "active" }); load(); };
  const del = async (t) => { if (window.confirm(`Delete "${t.company_name}" and ALL its data? This cannot be undone.`)) { await api.delete(`/tenants/${t.id}`); toast.success("Tenant deleted"); load(); } };
  const viewAs = (t) => { localStorage.setItem("act_tenant", t.id); localStorage.setItem("act_tenant_name", t.company_name); window.location.href = "/"; };

  return (
    <div className="min-h-screen bg-[hsl(var(--sidebar))] text-white">
      <header className="h-16 flex items-center justify-between px-8 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-destructive flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
          <span className="font-heading font-extrabold text-lg">OKRStaff <span className="text-white/50 font-normal">Platform</span></span>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10" data-testid="platform-logout"><LogOut className="h-4 w-4 mr-1.5" /> Sign out</Button>
      </header>
      <main className="max-w-5xl mx-auto p-8">
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="platform-overview">
            <Card className="p-4 rounded-md bg-white/5 border-white/10 text-white">
              <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><Building2 className="h-4 w-4" /> Tenants</div>
              <div className="mt-1.5 font-heading font-extrabold text-3xl" data-testid="stat-total-tenants">{totals.tenants}</div>
              <div className="text-xs text-white/50 mt-1">{totals.active_tenants} active · {totals.suspended_tenants} suspended</div>
            </Card>
            <Card className="p-4 rounded-md bg-white/5 border-white/10 text-white">
              <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><UserCheck className="h-4 w-4" /> Active Employees</div>
              <div className="mt-1.5 font-heading font-extrabold text-3xl" data-testid="stat-active-employees">{totals.active_employees}</div>
              <div className="text-xs text-white/50 mt-1">{totals.total_employees} total records</div>
            </Card>
            <Card className="p-4 rounded-md bg-white/5 border-white/10 text-white">
              <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><Clock className="h-4 w-4" /> Present Today</div>
              <div className="mt-1.5 font-heading font-extrabold text-3xl" data-testid="stat-present-today">{totals.present_today}</div>
              <div className="text-xs text-white/50 mt-1">across all tenants</div>
            </Card>
            <Card className="p-4 rounded-md bg-white/5 border-white/10 text-white">
              <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><FileClock className="h-4 w-4" /> Pending Leaves</div>
              <div className="mt-1.5 font-heading font-extrabold text-3xl" data-testid="stat-pending-leaves">{totals.pending_leaves}</div>
              <div className="text-xs text-white/50 mt-1">awaiting approval</div>
            </Card>
          </div>
        )}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline text-white/50">Super Admin</p>
            <h1 className="font-heading font-extrabold text-4xl mt-1">Tenants</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-destructive hover:bg-destructive/90" data-testid="create-tenant-btn"><Plus className="h-4 w-4 mr-1.5" /> New Tenant</Button></DialogTrigger>
            <DialogContent className="bg-popover text-foreground max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Create Tenant</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Company Name</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5" data-testid="tenant-name" /></div>
                <div><Label>Admin Name</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1.5" data-testid="tenant-admin-name" /></div>
                <div><Label>Admin Email (login)</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="mt-1.5" data-testid="tenant-admin-email" /></div>
                <div><Label>Temp Password</Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="mt-1.5" data-testid="tenant-admin-pw" /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!form.company_name || !form.admin_email || !form.admin_password} data-testid="tenant-save">Create Tenant</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {tenants.length === 0 && <p className="text-white/50 col-span-full py-12 text-center">No tenants yet. Create your first company.</p>}
          {tenants.map((t) => (
            <Card key={t.id} className="p-5 rounded-md bg-white/5 border-white/10 text-white" data-testid={`tenant-${t.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-bold text-lg">{t.company_name}</h3>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs border ${t.status === "active" ? "bg-success/20 text-success border-success/30" : "bg-destructive/20 text-destructive border-destructive/30"}`}>{t.status}</span>
                </div>
                <span className="inline-flex items-center gap-1 text-sm text-white/60"><Users className="h-4 w-4" /> {t.active_employees}/{t.employee_count}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="rounded bg-white/5 py-2">
                  <div className="font-heading font-bold text-lg" data-testid={`tenant-active-${t.id}`}>{t.active_employees}</div>
                  <div className="text-[10px] uppercase tracking-wide text-white/40">Active</div>
                </div>
                <div className="rounded bg-white/5 py-2">
                  <div className="font-heading font-bold text-lg" data-testid={`tenant-present-${t.id}`}>{t.present_today}</div>
                  <div className="text-[10px] uppercase tracking-wide text-white/40">Present</div>
                </div>
                <div className="rounded bg-white/5 py-2">
                  <div className="font-heading font-bold text-lg" data-testid={`tenant-pending-${t.id}`}>{t.pending_leaves}</div>
                  <div className="text-[10px] uppercase tracking-wide text-white/40">Pending</div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" className="bg-white text-[hsl(225_59%_11%)] hover:bg-white/90 flex-1" onClick={() => viewAs(t)} data-testid={`view-tenant-${t.id}`}><Eye className="h-4 w-4 mr-1.5" /> View as</Button>
                <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => toggle(t)} data-testid={`toggle-tenant-${t.id}`}><Power className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => del(t)} data-testid={`del-tenant-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
